import type { SettingsApiResponse, SettingsConfig } from '@startupquickstart/frontend';
import { StandardSidebar, useApiClient, useOrganization } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export function AppStandardSidebar() {
  const apiClient = useApiClient();
  const { organizationSelected } = useOrganization();

  // Fetch settings data using React Query
  const { data: userSettingsConfig } = useQuery({
    queryKey: ['user-settings-config'],
    queryFn: () => apiClient.get<SettingsApiResponse<SettingsConfig>>('/settings/config/user'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const { data: orgSettingsConfig } = useQuery({
    queryKey: ['org-settings-config'],
    queryFn: () =>
      apiClient.get<SettingsApiResponse<SettingsConfig>>('/settings/config/organization'),
    enabled: !organizationSelected, // Only fetch when in organization context
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Create memoized functions to avoid recreating on every render
  const checkUserSettings = useMemo(() => {
    return () => {
      if (!userSettingsConfig) return { success: false, data: { enabled: false } };
      return {
        success: userSettingsConfig.success,
        data: {
          enabled: userSettingsConfig.data?.enabled ?? false,
        },
      };
    };
  }, [userSettingsConfig]);

  const checkOrgSettings = useMemo(() => {
    return () => {
      if (!orgSettingsConfig) return { success: false, data: { enabled: false } };
      return {
        success: orgSettingsConfig.success,
        data: {
          enabled: orgSettingsConfig.data?.enabled ?? false,
        },
      };
    };
  }, [orgSettingsConfig]);

  return (
    <StandardSidebar
      variant="inset"
      logoSrc="/assets/images/logos/logo-hunts.svg"
      checkUserSettings={checkUserSettings}
      checkOrgSettings={checkOrgSettings}
      organizationSelected={organizationSelected}
    />
  );
}
